import type * as EditMemberAPI from 'ab-api/web/account/editMember';

import type AccountVM from 'ab-viewModels/account/member.viewModel';

import type InviteAccountRM from 'ab-requestModels/account/invite.requestModel';
import type CreateAccountRM from 'ab-requestModels/account/upsert.requestModel';

import { getId } from '@acceligentllc/shared/utils/array';
import { toRawPhoneNumber } from '@acceligentllc/shared/utils/phone';
import * as TimeUtils from '@acceligentllc/shared/utils/time';

import type { AccountPermission } from '@acceligentllc/shared/enums/accountPermission';
import type { AccountPermissionTemplate } from '@acceligentllc/shared/enums/accountPermissionTemplate';
import { AccountPermissionTemplateOptions } from '@acceligentllc/shared/enums/accountPermissionTemplateOptions';
import { TimeFormat } from '@acceligentllc/shared/enums/timeFormat';

interface UserFM {
	uniqueId: string;
	firstName: string;
	lastName: string;
	email: Nullable<string>;
	phoneNumber: Nullable<string>;
	extension: Nullable<string>;
	imageUrl: Nullable<string>;
}

export interface ExistingUserFM {
	id: number;
	name: string;
	email: Nullable<string>;
	phoneNumber: Nullable<string>;
	activeInLMS: boolean;
}

interface UserGroupFM {
	id: number;
	name: string;
}

interface LocationFM {
	id: number;
	nickname: string;
}

export default class AccountFM {
	id?: number;
	userId?: number;
	user?: UserFM;
	existingUserId?: number;
	existingUser?: ExistingUserFM;
	inviteOption?: typeof AccountFM.INVITE_OPTIONS[0] | null;
	deleteImage: boolean;
	locationId: Nullable<number>;
	location: Nullable<LocationFM>;
	accountTemplateId: AccountPermissionTemplate;
	accountTemplate: typeof AccountPermissionTemplateOptions[0] | undefined;
	assignableToWorkOrder: boolean;
	assignableAsSuperintendent: boolean;
	assignableAsProjectManager: boolean;
	assignableAsQAQC: boolean;
	assignableAsAccounting: boolean;
	assignableAsManagement: boolean;
	assignableAsTechnician: boolean;
	permissions: Record<AccountPermission, boolean>;
	userGroups: UserGroupFM[];
	activateImmediately: boolean;
	activeInLMS: boolean;
	excludeFromNotifyAll: boolean;
	sendEmail: boolean;
	sendSms: boolean;
	agreeToSendSms: boolean;
	isDeleted: Nullable<boolean>;
	/** YYYY-MM-DD */
	waiverStatusValidFrom: Nullable<string>;

	static CREATE_NEW_USER_OPTION = { value: 0, label: 'Create new user' };
	static INVITE_USER_OPTION = { value: 1, label: 'Add to existing user' };

	static INVITE_OPTIONS = [
		AccountFM.CREATE_NEW_USER_OPTION,
		AccountFM.INVITE_USER_OPTION,
	];

	static getTemplateOption(template: AccountPermissionTemplate) {
		return AccountPermissionTemplateOptions.find((_option) => _option.id === template);
	}

	static fromViewModel(model: AccountVM): AccountFM {
		return {
			id: model.id,
			userId: model.userId,
			user: model.user,
			locationId: model.locationId,
			location: model.location,
			deleteImage: false,
			assignableToWorkOrder: model.assignableToWorkOrder,
			assignableAsSuperintendent: model.assignableAsSuperintendent,
			assignableAsProjectManager: model.assignableAsProjectManager,
			assignableAsQAQC: model.assignableAsQAQC,
			assignableAsAccounting: model.assignableAsAccounting,
			assignableAsManagement: model.assignableAsManagement,
			assignableAsTechnician: model.assignableAsTechnician,
			permissions: model.permissions,
			userGroups: model.userGroups,
			accountTemplateId: model.accountTemplate,
			accountTemplate: AccountFM.getTemplateOption(model.accountTemplate),
			activeInLMS: model.user.activeInLMS,
			excludeFromNotifyAll: model.excludeFromNotifyAll,
			activateImmediately: false,
			sendEmail: false,
			sendSms: false,
			agreeToSendSms: !!model.user.phoneNumber,
			inviteOption: null,
			isDeleted: model.isDeleted,
			waiverStatusValidFrom: model.waiverStatusValidFrom
				? TimeUtils.formatDate(model.waiverStatusValidFrom, TimeFormat.DATE_ONLY, TimeFormat.DB_DATE_ONLY)
				: null,
		};
	}

	static toCreateRequestModel(form: AccountFM): CreateAccountRM {
		if (!form.user) {
			throw new Error('User not defined');
		}

		return {
			id: form.id,
			user: {
				...form.user,
				firstName: form.user.firstName.trimEnd(),
				lastName: form.user.lastName.trimEnd(),
				phoneNumber: form.user?.phoneNumber ? toRawPhoneNumber(form.user.phoneNumber) : null,
				activeInLMS: form.activeInLMS,
				waiverStatusValidFrom: form.waiverStatusValidFrom
					? TimeUtils.formatDate(form.waiverStatusValidFrom, TimeFormat.DB_DATE_ONLY, TimeFormat.DATE_ONLY)
					: null,
			},
			imageUrl: form.user?.imageUrl ?? null,
			deleteImage: form.deleteImage,
			locationId: form.locationId,
			accountTemplate: form.accountTemplateId,
			assignableToWorkOrder: form.assignableToWorkOrder,
			assignableAsSuperintendent: form.assignableAsSuperintendent,
			assignableAsProjectManager: form.assignableAsProjectManager,
			assignableAsQAQC: form.assignableAsQAQC,
			assignableAsAccounting: form.assignableAsAccounting,
			assignableAsManagement: form.assignableAsManagement,
			assignableAsTechnician: form.assignableAsTechnician,
			excludeFromNotifyAll: form.excludeFromNotifyAll,
			permissions: form.permissions,
			userGroupIds: form.userGroups?.map(getId) ?? [],
			activateImmediately: form.activateImmediately,
			sendEmail: form.sendEmail,
			sendSms: form.sendSms,
			isDeleted: form.isDeleted,
		};
	}

	static toEditRM(form: AccountFM): EditMemberAPI.W_Account_EditMember_RM {
		if (!form.user || !form.id) {
			throw new Error('User not defined');
		}

		return {
			id: form.id,
			user: {
				id: form.id,
				...form.user,
				firstName: form.user.firstName.trimEnd(),
				lastName: form.user.lastName.trimEnd(),
				phoneNumber: form.user?.phoneNumber ? toRawPhoneNumber(form.user.phoneNumber) : null,
				activeInLMS: form.activeInLMS,
			},
			imageUrl: form.user?.imageUrl ?? null,
			deleteImage: form.deleteImage,
			locationId: form.locationId,
			accountTemplate: form.accountTemplateId,
			assignableToWorkOrder: form.assignableToWorkOrder,
			assignableAsSuperintendent: form.assignableAsSuperintendent,
			assignableAsProjectManager: form.assignableAsProjectManager,
			assignableAsQAQC: form.assignableAsQAQC,
			assignableAsAccounting: form.assignableAsAccounting,
			assignableAsManagement: form.assignableAsManagement,
			assignableAsTechnician: form.assignableAsTechnician,
			excludeFromNotifyAll: form.excludeFromNotifyAll,
			permissions: form.permissions,
			userGroupIds: form.userGroups?.map(getId) ?? [],
			activateImmediately: form.activateImmediately,
			sendEmail: form.sendEmail,
			sendSms: form.sendSms,
			isDeleted: form.isDeleted,
			waiverStatusValidFrom: form.waiverStatusValidFrom
				? TimeUtils.formatDate(form.waiverStatusValidFrom, TimeFormat.DB_DATE_ONLY, TimeFormat.DATE_ONLY)
				: null,
		};
	}

	static toInviteRequestModel(form: AccountFM): InviteAccountRM {
		return {
			existingUserId: form.existingUserId,
			locationId: form.locationId,
			accountTemplate: form.accountTemplateId,
			assignableToWorkOrder: form.assignableToWorkOrder,
			assignableAsSuperintendent: form.assignableAsSuperintendent,
			assignableAsProjectManager: form.assignableAsProjectManager,
			assignableAsQAQC: form.assignableAsQAQC,
			assignableAsAccounting: form.assignableAsAccounting,
			assignableAsManagement: form.assignableAsManagement,
			assignableAsTechnician: form.assignableAsTechnician,
			excludeFromNotifyAll: form.excludeFromNotifyAll,
			permissions: form.permissions,
			userGroupIds: form.userGroups?.map(getId) ?? [],
			activateImmediately: form.activateImmediately,
			activeInLMS: form.activeInLMS,
			sendEmail: form.sendEmail,
			sendSms: form.sendSms,
		};
	}
}
